import React, { useState, useEffect } from 'react';
import { User, Mail, Shield, Bell, Key, LogOut } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import EditProfileModal from './EditProfileModal';
import IdentityVerification from './IdentityVerification';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function Profile({ user: appUser, refreshUser }) { // Accept props
    // We can still keep local state if we want to fetch independently, 
    // BUT best to use appUser if provided.
    const [localUser, setLocalUser] = useState(null);
    const [loading, setLoading] = useState(!appUser); // If appUser exists, not loading
    const [showEditModal, setShowEditModal] = useState(false);

    const [activeTab, setActiveTab] = useState('profile');

    // Use appUser if available, otherwise localUser
    const user = appUser || localUser;

    useEffect(() => {
        // If no appUser provided (e.g. standalone access?), fetch it.
        // Or if we just want to ensure fresh data on mount.
        if (!appUser) {
            const fetchProfile = async () => {
                try {
                    const token = localStorage.getItem('token');
                    if (!token) return;

                    const res = await axios.get(`${API_BASE}/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setLocalUser(res.data);
                } catch (err) {
                    console.error("Failed to fetch profile", err);
                    toast.error("Could not load profile");
                } finally {
                    setLoading(false);
                }
            };
            fetchProfile();
        } else {
            setLoading(false);
        }
    }, [appUser]);

    const handleUpdate = () => {
        if (refreshUser) {
            refreshUser(); // Updates App.js state -> updates Sidebar & Profile
        } else {
            // Fallback for standalone
            window.location.reload();
        }
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.reload(); // Force reload to show login screen
    };

    if (loading) return <div className="p-8 text-center text-slate-500">Loading profile...</div>;

    if (!user) return (
        <div className="p-8 text-center">
            <p className="text-red-500 mb-4">Session expired or invalid.</p>
            <button
                onClick={handleLogout}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
            >
                Log In Again
            </button>
        </div>
    );

    // Improved display name logic
    const displayName = user.name || user.email.split('@')[0].replace(/[._]/g, ' ');
    const initials = displayName.substring(0, 2).toUpperCase();
    const jobTitle = user.title || user.role || "User";



    return (
        <div className="max-w-4xl mx-auto space-y-8">

            {/* Header / Banner */}
            <div className="relative">
                <div className="h-48 rounded-2xl bg-gradient-to-r from-brand-600 to-purple-600 overflow-hidden shadow-lg">
                    <div className="absolute inset-0 bg-black/10" />
                </div>
                <div className="absolute -bottom-12 left-8 flex items-end">
                    <div className="p-1 bg-white dark:bg-slate-900 rounded-full">
                        <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center text-3xl font-bold text-slate-500 border-4 border-white dark:border-slate-900 overflow-hidden">
                            {user.is_verified ? (
                                <img src={`https://ui-avatars.com/api/?name=${displayName}&background=random`} alt="Profile" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-brand-600">{initials}</span>
                            )}
                        </div>
                        {user.is_verified && (
                            <div className="absolute bottom-1 right-1 bg-green-500 text-white p-1 rounded-full border-2 border-white dark:border-slate-900" title="Verified">
                                <Shield className="w-3 h-3 fill-current" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="pt-10 px-2 flex justify-between items-start">
                <div className="ml-6">
                    <div className="flex items-center gap-2">
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white capitalize">{displayName}</h1>
                        {user.is_verified && <Shield className="w-5 h-5 text-green-500 fill-current" />}
                    </div>
                    <p className="text-slate-500 dark:text-slate-400">{jobTitle}</p>
                </div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'profile' ? 'bg-slate-100 text-slate-900 dark:bg-slate-700 dark:text-white' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'}`}
                    >
                        Profile
                    </button>
                    <button
                        onClick={() => setActiveTab('verification')}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${activeTab === 'verification' ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300' : 'text-slate-500 hover:text-slate-700 dark:text-slate-400'}`}
                    >
                        <Shield className="w-4 h-4" /> Verification
                    </button>
                    <button
                        onClick={() => setShowEditModal(true)}
                        className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                    >
                        Edit
                    </button>
                </div>
            </div>

            {activeTab === 'profile' ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left Column: Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {user.bio && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700">
                                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">About</h3>
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">{user.bio}</p>
                            </div>
                        )}

                        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Personal Information</h3>
                            <div className="space-y-4">
                                <div className="flex items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                                    <User className="w-5 h-5 text-slate-400 mr-3" />
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase font-semibold">Account ID</p>
                                        <p className="text-slate-700 dark:text-slate-200 font-mono text-xs">{user._id}</p>
                                    </div>
                                </div>

                                <div className="flex items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                                    <Mail className="w-5 h-5 text-slate-400 mr-3" />
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase font-semibold">Email Address</p>
                                        <p className="text-slate-700 dark:text-slate-200 font-medium">{user.email}</p>
                                    </div>
                                </div>

                                {user.phone && (
                                    <div className="flex items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                                        <Key className="w-5 h-5 text-slate-400 mr-3" />
                                        <div>
                                            <p className="text-xs text-slate-500 uppercase font-semibold">Phone</p>
                                            <p className="text-slate-700 dark:text-slate-200 font-medium">{user.phone}</p>
                                        </div>
                                    </div>
                                )}

                                <div className="flex items-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50">
                                    <Shield className="w-5 h-5 text-slate-400 mr-3" />
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase font-semibold">Role</p>
                                        <p className="text-slate-700 dark:text-slate-200 font-medium capitalize">{user.role || "User"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Activity Summary */}
                        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Activity Summary</h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-900/20 border border-brand-100 dark:border-brand-900/30">
                                    <p className="text-2xl font-bold text-brand-700 dark:text-brand-300">Active</p>
                                    <p className="text-sm text-brand-600/80 dark:text-brand-400">Account Status</p>
                                </div>
                                <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-900/30">
                                    <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">Basic</p>
                                    <p className="text-sm text-purple-600/80 dark:text-purple-400">Subscription Tier</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Settings */}
                    <div className="space-y-6">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-sm border border-slate-100 dark:border-slate-700">
                            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Account Settings</h3>
                            <div className="space-y-2">
                                <button
                                    onClick={() => toast.success('Notifications enabled', { icon: '🔔' })}
                                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left pt-3"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                                            <Bell className="w-4 h-4" />
                                        </div>
                                        <span className="font-medium text-slate-700 dark:text-slate-200">Notifications</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => toast('Password reset link sent!', { icon: '📧' })}
                                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-100 dark:bg-slate-700 rounded-full text-slate-600 dark:text-slate-300">
                                            <Key className="w-4 h-4" />
                                        </div>
                                        <span className="font-medium text-slate-700 dark:text-slate-200">Change Password</span>
                                    </div>
                                </button>
                                <button
                                    onClick={handleLogout}
                                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left group"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-400 group-hover:bg-red-200 dark:group-hover:bg-red-900/50">
                                            <LogOut className="w-4 h-4" />
                                        </div>
                                        <span className="font-medium text-red-600 dark:text-red-400">Sign Out</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="max-w-2xl mx-auto bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-sm border border-slate-100 dark:border-slate-700">
                    <IdentityVerification user={user} onVerificationComplete={handleUpdate} />
                </div>
            )}

            <EditProfileModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                user={user}
                onUpdate={handleUpdate}
            />
        </div>
    );
}
